import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useEffect,
  useMemo,
} from "react";
import { useSelector } from "react-redux";
import { Container } from "@pixi/react";
import { useDimensions } from "../../../Contexts/DimensionsContext";
import WinTextController from "./WinTextController";
import { safeInit } from "../../../utilities";
import {
  selectWinAnimationsPlaying,
  selectIsAnimatedWin,
} from "../../../State/selectors";
import { selectWinAnimationCounterFinished } from "../../../State/Slices/animationsSlice";

const WinText = forwardRef(
  (
    { fontSizeMultiplier = 1.0, yPosition = null, hideOnBigWin = false },
    ref,
  ) => {
    const containerRef = useRef(null);
    const controllerRef = useRef(null);
    const { symbolWidth, symbolHeight } = useDimensions();
    const winAnimationsPlaying = useSelector(selectWinAnimationsPlaying);
    const isAnimatedWin = useSelector(selectIsAnimatedWin);
    const winAnimationCounterFinished = useSelector(selectWinAnimationCounterFinished);

    // Default Y: dead center of the reelset grid.
    // The overlay_reel_container origin (y=0) is the grid center.
    const containerY = useMemo(
      () => (yPosition !== null ? yPosition : 0),
      [yPosition],
    );

    // Initialize controller when container is ready (after render completes)
    useEffect(() => {
      if (containerRef.current && !controllerRef.current) {
        safeInit(() => {
          controllerRef.current = new WinTextController(
            containerRef.current,
            symbolWidth,
            symbolHeight,
            fontSizeMultiplier,
          );
        });
      }

      return () => {
        if (controllerRef.current) {
          controllerRef.current.cleanup();
          controllerRef.current = null;
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Handle dimension changes
    useEffect(() => {
      if (controllerRef.current && symbolWidth && symbolHeight) {
        controllerRef.current.updateDimensions(symbolWidth, symbolHeight);
      }
    }, [symbolWidth, symbolHeight]);

    useImperativeHandle(ref, () => ({
      showWinAmount: (amount, options) => {
        controllerRef.current?.showWinAmount(amount, options);
      },
      playOut: () => {
        return controllerRef.current?.playOut() || Promise.resolve();
      },
      hide: () => {
        controllerRef.current?.hide();
      },
      getSpineInstance: () => {
        return controllerRef.current?.getSpineInstance();
      },
    }));

    // Determine alpha based on hideOnBigWin prop
    // Use alpha 0.001 instead of visible=false so the animation cycle keeps running in the background
    // Show win lines as soon as the counter finishes (don't wait for entire popup to close)
    const containerAlpha = useMemo(() => {
      if (hideOnBigWin) {
        // Hide during big win animations UNTIL the Big Win counter finishes
        const shouldHide = isAnimatedWin && winAnimationsPlaying && !winAnimationCounterFinished;
        return shouldHide ? 0.001 : 1;
      }
      return 1;
    }, [hideOnBigWin, isAnimatedWin, winAnimationsPlaying, winAnimationCounterFinished]);

    return (
      <Container
        ref={containerRef}
        y={containerY}
        x={0}
        zIndex={6}
        sortableChildren
        name={`win_text_${fontSizeMultiplier}`}
        alpha={containerAlpha}
      />
    );
  },
);

export default WinText;
